import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, type ReactNode } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFirebaseAuthReady } from "../Services/FirebaseAuthService";
import { JobRefreshCoordinator, jobKey, type JobSignal } from "../Services/JobRefreshCoordinator";
import { insightsApi, insightJobTags } from "../features/insights/api";
import { jobRegistry } from "../Services/JobRegistry";
import { listenToJobSignals } from "../Services/JobSignalService";
import { getUserFromLS } from "../utils/getUserFromLS";
import { useWorkspaceClient } from "./WorkspaceClientContext";
import { invalidateAccountFirstPages } from "../hooks/useAccountQuery";
import { assetMonitoringApi, scopeTag } from "../features/asset-monitoring/api";
import { SMARTLEAD_QUEUE_CHANGED } from "../Api/smartlead";

const Context = createContext<JobRefreshCoordinator | null>(null);
export const useJobChanges = () => useContext(Context);

export function JobChangesProvider({ children }: { children: ReactNode }) {
  const dispatch = useDispatch();
  const auth = useSelector((state: { auth: { isAuthenticated: boolean; user?: { id?: string } } }) => state.auth);
  const userId = auth.isAuthenticated ? auth.user?.id || getUserFromLS()?.id : undefined;
  const { effectiveClientId } = useWorkspaceClient();
  const ready = useFirebaseAuthReady(userId);
  const coordinator = useMemo(() => new JobRefreshCoordinator(), [userId, effectiveClientId]);
  const connected = useRef(false);
  const pending = useMemo(() => new Map<string, JobSignal>(), [coordinator]);
  const flush = useCallback(() => {
    if (document.hidden || !userId) return;
    const insightClients = new Set<string>();
    const accountClients = new Set<string>();
    const assetClients = new Set<string>();
    const smartleadClients = new Set<string>();
    for (const signal of pending.values()) {
      const registration = jobRegistry[signal.job.kind as keyof typeof jobRegistry];
      if (!registration || registration.owner !== signal.job.owner) continue;
      if (signal.job.kind === "insight_run" && signal.resources.some(resource => registration.resources.includes(resource.kind)))
        insightClients.add(signal.client_id);
      if (["account_bulk", "account_deletion"].includes(signal.job.kind) && signal.resources.some(resource => registration.resources.includes(resource.kind)))
        accountClients.add(signal.client_id);
      if (signal.job.kind === "asset_run" && signal.resources.some(resource => registration.resources.includes(resource.kind)))
        assetClients.add(signal.client_id);
      if (signal.job.kind === "smartlead_submission")
        smartleadClients.add(signal.client_id);
    }
    pending.clear();
    for (const clientId of accountClients) invalidateAccountFirstPages(clientId);
    for (const clientId of assetClients)
      dispatch(assetMonitoringApi.util.invalidateTags([{ type: "Monitoring", id: scopeTag({ clientId, actorId: userId }) }]));
    for (const clientId of smartleadClients)
      window.dispatchEvent(new CustomEvent(SMARTLEAD_QUEUE_CHANGED, { detail: { clientId } }));
    for (const clientId of assetClients)
      window.dispatchEvent(new CustomEvent("intentico:asset-job-changed", { detail: { clientId } }));
    for (const clientId of insightClients)
      dispatch(insightsApi.util.invalidateTags(insightJobTags({ clientId, actorId: userId })));
  }, [dispatch, pending, userId]);

  useLayoutEffect(() => {
    coordinator.setVisible(false);
    return () => coordinator.dispose();
  }, [coordinator]);

  useLayoutEffect(() => {
    if (!userId) return;
    // API access is independent of Firebase sign-in. An unavailable listener
    // must still allow authoritative reads and the bounded recovery window.
    if (!ready) { coordinator.setVisible(!document.hidden); return; }
    connected.current = false;
    let active = true;
    const stop = listenToJobSignals(userId, signal => {
      if (coordinator.changed(signal) && (!effectiveClientId || signal.client_id === effectiveClientId)) {
        pending.set(jobKey(signal.job), signal);
        queueMicrotask(() => { if (active) flush(); });
      }
    }, online => {
      if (online && !connected.current) coordinator.reconcile();
      connected.current = online;
    }, signal => {
      coordinator.reconcile(signal.job);
      if (!effectiveClientId || signal.client_id === effectiveClientId) {
        pending.set(jobKey(signal.job), signal);
        queueMicrotask(() => { if (active) flush(); });
      }
    });
    // The listener is installed before any observer's initial reconciliation.
    coordinator.setVisible(!document.hidden);
    return () => { active = false; stop(); pending.clear(); };
  }, [userId, ready, effectiveClientId, coordinator, pending, flush]);

  useEffect(() => {
    const visible = () => { coordinator.setVisible(!!userId && !document.hidden); flush(); };
    const focus = () => { coordinator.reconcile(); flush(); };
    document.addEventListener("visibilitychange", visible);
    window.addEventListener("focus", focus);
    return () => {
      document.removeEventListener("visibilitychange", visible);
      window.removeEventListener("focus", focus);
    };
  }, [coordinator, flush, userId]);

  return <Context.Provider value={coordinator}>{children}</Context.Provider>;
}
